/**
 * An object representation of shopper data extracted from the info_idp cookie if present.
 * Note: Additional properties may be available
 *
 * @typedef {Object} InitialShopperData
 * @property {string} shopperid The Shopper ID
 * @property {string} auth The auth-type that the user signed in with
 * @property {string} username The username of the current user
 * @property {string} info The raw info_idp value
 * @property {string} firstname The user's first name
 * @property {string} lastname The user's last name
 */

/**
 * An object representation of the full shopper data, merging the latest data from the
 * info_idp cookie with the results of calling GUI to get the latest information, if necessary
 * Note: Additional properties may be available
 *
 * @typedef {InitialShopperData} FullShopperData
 * @property {Array} tms The TMS values
 * @property {string} fullname The user's full name
 */

/**
 * Callback function to get notified about shopper data updates
 * @callback ShopperUpdate
 * @param {Error} error Any error that occurred
 * @param {FullShopperData} data The full shopper data after update
 */

/**
 * Shallow copies the properties of the source into the target
 * @param {Object} target Where the properties should be copied
 * @param {Object} source Where the properties come from
 */
function copyInto(target, source) {
  Object.keys(source).reduce(function (acc, key) {
    acc[key] = source[key];
    return acc;
  }, target);
}

/**
 * Class to provide synchronous access to some shopperData, with optional updates via callback with the full data.
 *
 * @class SyncAccess
 */
export default class SyncAccess {
  constructor() {
    this.data = this.data.bind(this);
    this._cbs = [];
    this._data = null;
    this._updated = false;
  }

  /**
   * Interface method, should be implemented by inheriting class.
   * Return the defaults to be used for `this._data`.
   *
   * @returns {Object} data.
   * @private
   */
  get defaults() {
    return {};
  }

  /**
   * Interface method, should be implemented by inheriting class.
   * Transform the data after it has been fetched.
   *
   * @param {Object} data Data to be transformed.
   * @returns {Object} transformed data.
   * @private
   */
  transform(data) {
    return data;
  }

  /**
   * Interface method, should be implemented by inheriting class.
   * Fetch data from API and callback.
   *
   * @param {function} done completion callback.
   * @private
   */
  fetch(done = () => {}) {
    done();
  }

  /**
   * Fetch and update the shopper data. Notify any callback listeners of the update
   *
   * @private
   */
  updateWithShopperData() {
    this.fetch((error, data = {}) => {
      if (error) return;

      if (this._data) {
        // Copy the data over into the existing object so that it updates the existing object we
        // already returned with the new data.
        copyInto(this._data, this.transform(data));
        copyInto(this._data, data);

        this._updated = true;
        const cbs = this._cbs;
        this._cbs = [];
        for (let i = 0; i < cbs.length; i++) {
          cbs[i](null, this._data);
        }
      }
    });
  }

  /**
   * Provides synchronous access to shopperData
   * @param {ShopperUpdate} [onUpdate] Callback mechanism to receive updates once full shopperData is loaded.
   * If shopperData was already loaded, this won't be called.
   * @returns {InitialShopperData|FullShopperData} The latest shopperData available, may be out of date based on cookie values.
   * If shopperData was already loaded, it will be the full & correct data
   * @public
   */
  data(onUpdate) {
    if (!this._updated && onUpdate) {
      this._cbs.push(onUpdate);
    }

    if (this._data) {
      return this._data;
    }

    this._data = this.transform(this.defaults);

    // Kick off data updates
    this.updateWithShopperData();

    return this._data;
  }
}
